import React, { useState, useEffect, useRef } from 'react';
import { triggerStockLimitAlert } from '../utils/firebaseService';

export default function SearchDrawer({ isOpen, onClose, products, setCartItems, setSelectedProduct, setView }) {
  const [query, setQuery] = useState('');
  const [placeholderText, setPlaceholderText] = useState('Search for Rings...');
  const [addedProductIds, setAddedProductIds] = useState({});
  const inputRef = useRef(null);

  // Typewriter placeholder animation next to "Search for "
  useEffect(() => {
    const terms = [
      'Rings...',
      'Earrings...',
      'Necklaces...',
      'Pendants...',
      'Bracelets...',
      'Sets...',
      'Polki Heirlooms...',
      'Eternal Moissanite...',
      'Royal Meenakari...',
      'Jaipur Blue Pottery...'
    ];
    
    let termIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let typingSpeed = 180; // ms per letter (decreased speed)
    let active = true;
    let timeoutId = null;
    
    const tick = () => {
      if (!active) return;
      
      const currentTerm = terms[termIndex];
      
      if (isDeleting) {
        charIndex--;
      } else {
        charIndex++;
      }
      
      const visibleText = currentTerm.substring(0, charIndex);
      setPlaceholderText(`Search for ${visibleText}`);
      
      let nextSpeed = typingSpeed;
      if (isDeleting) {
        nextSpeed = 80; // deleting is faster but slower than before
      }
      
      if (!isDeleting && charIndex === currentTerm.length) {
        nextSpeed = 2000; // pause at end of word
        isDeleting = true;
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        termIndex = (termIndex + 1) % terms.length;
        nextSpeed = 500; // pause before typing next word
      }
      
      timeoutId = setTimeout(tick, nextSpeed);
    };
    
    timeoutId = setTimeout(tick, 200);
    
    return () => {
      active = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  // Esc key listener to close drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus search input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 150);
    } else {
      setQuery('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Search filter query logic
  const filteredProducts = query.trim() === ''
    ? []
    : products.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(query.toLowerCase()))
      );

  const recommendations = products.slice(0, 3);

  const handleAddToCart = (product) => {
    const liveProd = (products || []).find(p => String(p.id) === String(product.id));
    const maxStock = (liveProd && typeof liveProd.stock === 'number')
      ? liveProd.stock
      : (typeof product.stock === 'number' ? product.stock : 10);

    if (maxStock <= 0) {
      triggerStockLimitAlert(0);
      return;
    }

    let limitReached = false;

    setCartItems(prev => {
      const existing = prev.find(item => String(item.id) === String(product.id));
      const currentQty = existing ? existing.qty : 0;
      if (currentQty + 1 > maxStock) {
        limitReached = true;
        triggerStockLimitAlert(maxStock);
        if (existing) {
          return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: maxStock } : item);
        } else {
          return prev;
        }
      }
      if (existing) {
        return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, stock: maxStock, qty: 1 }];
    });
    
    if (!limitReached) {
      // Set success tick state for 2 seconds
      setAddedProductIds(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedProductIds(prev => {
          const next = { ...prev };
          delete next[product.id];
          return next;
        });
      }, 2000);
    }
  };

  return (
    <div className="fixed inset-x-0 top-0 z-[100] bg-white shadow-2xl border-b border-[#584738]/20 flex flex-col transition-all duration-300 ease-in-out">
      
      {/* Search Input Bar (Matches Aurus white header overlay styling) */}
      <div className="max-w-7xl mx-auto w-full px-6 md:px-12 py-4 md:py-6 flex items-center justify-between gap-4">
        
        {/* Left: Search Icon */}
        <div className="text-[#584738]/60 flex-shrink-0">
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" strokeWidth="1.25" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Center: Input with cycling placeholders */}
        <div className="flex-1 pl-2 md:pl-4">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholderText}
            className="w-full bg-transparent text-left font-serif text-lg md:text-2xl font-light text-[#584738] focus:outline-none placeholder-[#584738]/40 border-b border-[#584738]/10 pb-1"
          />
        </div>

        {/* Right: Close Button (Clean X Icon) */}
        <button
          onClick={onClose}
          className="p-1 text-[#584738]/70 hover:text-[#584738] focus:outline-none transition-colors duration-200 flex-shrink-0"
          aria-label="Close Search"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Instant Search Results Panel */}
      {query.trim() !== '' && (
        <div className="bg-[#FAF8F5] max-h-[75vh] overflow-y-auto w-full border-t border-[#584738]/5">
          <div className="max-w-6xl mx-auto px-4 md:px-12 py-8 md:py-4">
            <h4 className="font-serif text-[#584738]/60 text-xs tracking-widest uppercase mb-6 md:mb-2 text-center">
              Search Results ({filteredProducts.length} Designs Found)
            </h4>

            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {filteredProducts.map((product) => {
                  const liveProd = (products || []).find(p => String(p.id) === String(product.id)) || product;
                  const currentStock = typeof liveProd.stock === 'number' ? liveProd.stock : 10;
                  const isOutOfStock = currentStock <= 0;

                  return (
                    <div key={product.id} className="bg-white border border-[#584738]/10 p-2.5 md:p-3 rounded-xs flex flex-col justify-between group hover:shadow-md transition-shadow duration-300 relative">
                      {isOutOfStock && (
                        <div className="absolute top-2 right-2 z-10 bg-[#584738]/90 text-[#F5E6D8] text-[9px] font-sans font-bold tracking-wider px-2 py-0.5 uppercase shadow-sm">
                          Out of Stock
                        </div>
                      )}
                      <div 
                        onClick={() => { setSelectedProduct(liveProd); setView('product'); onClose(); }}
                        className="aspect-square w-full overflow-hidden rounded-xs bg-gray-50 mb-2.5 md:mb-3 relative cursor-pointer"
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          className={`w-full h-full object-cover group-hover:scale-102 transition-transform duration-500 ${isOutOfStock ? 'opacity-75 grayscale' : ''}`}
                        />
                      </div>
                      <div className="text-center flex flex-col flex-1 justify-between">
                        <div>
                          <h5 
                            onClick={() => { setSelectedProduct(liveProd); setView('product'); onClose(); }}
                            className="font-serif text-[#584738] text-xs md:text-[14px] font-medium tracking-wide mb-0.5 cursor-pointer hover:underline transition-all duration-200"
                          >
                            {product.name}
                          </h5>
                          <p 
                            className="font-sans text-[9px] md:text-[10px] text-[#584738]/60 mb-1.5 md:mb-1.5 leading-relaxed max-w-[200px] mx-auto font-light line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: product.description || '' }}
                          />
                          <p className="font-sans text-[11px] md:text-xs text-[#584738] font-semibold mb-2.5 md:mb-3">
                            ₹{product.price.toLocaleString()}
                          </p>
                        </div>
                        {isOutOfStock ? (
                          <button
                            disabled={true}
                            className="w-full py-1.5 md:py-2.5 text-[9px] md:text-[10px] tracking-widest font-sans uppercase font-light bg-neutral-300 text-neutral-500 cursor-not-allowed flex items-center justify-center"
                          >
                            Out of Stock
                          </button>
                        ) : (
                          <button
                            onClick={() => handleAddToCart(liveProd)}
                            disabled={addedProductIds[product.id]}
                            className={`w-full py-1.5 md:py-2.5 text-[9px] md:text-[10px] tracking-widest font-sans uppercase font-light transition-all duration-300 flex items-center justify-center gap-1.5 ${
                              addedProductIds[product.id] 
                                ? 'bg-[#3E5C43] text-white cursor-default' 
                                : 'bg-[#584738] text-white hover:bg-black'
                            }`}
                          >
                            {addedProductIds[product.id] ? (
                              <>
                                <svg className="w-3 h-3 stroke-current animate-bounce" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Added</span>
                              </>
                            ) : (
                              <span>Add to Bag</span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="font-serif text-lg text-[#584738]/80 mb-2">
                  We couldn't find any matches for "{query}"
                </p>
                <p className="font-sans text-xs text-[#584738]/50 mb-8 max-w-sm mx-auto leading-relaxed">
                  Please check the spelling or try searching for category terms like "Necklace", "Pendant", "Ceramic", or "Temple".
                </p>

                {/* Best Sellers Recommendations Grid */}
                <div className="border-t border-[#584738]/10 pt-8 md:pt-4">
                  <h5 className="font-serif text-[#584738] text-xs tracking-widest uppercase mb-6 md:mb-3">
                    Check Out Our Best Sellers
                  </h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
                    {recommendations.map((product) => {
                      const liveProd = (products || []).find(p => String(p.id) === String(product.id)) || product;
                      const currentStock = typeof liveProd.stock === 'number' ? liveProd.stock : 10;
                      const isOutOfStock = currentStock <= 0;

                      return (
                        <div key={product.id} className="bg-white border border-[#584738]/10 p-2.5 md:p-3 rounded-xs flex flex-col justify-between group hover:shadow-md transition-shadow duration-300 relative">
                          {isOutOfStock && (
                            <div className="absolute top-2 right-2 z-10 bg-[#584738]/90 text-[#F5E6D8] text-[9px] font-sans font-bold tracking-wider px-2 py-0.5 uppercase shadow-sm">
                              Out of Stock
                            </div>
                          )}
                          <div 
                            onClick={() => { setSelectedProduct(liveProd); setView('product'); onClose(); }}
                            className="aspect-square w-full overflow-hidden rounded-xs bg-gray-50 mb-2 md:mb-2 cursor-pointer"
                          >
                            <img
                              src={product.image}
                              alt={product.name}
                              className={`w-full h-full object-cover ${isOutOfStock ? 'opacity-75 grayscale' : ''}`}
                            />
                          </div>
                          <div className="text-center">
                            <h6 
                              onClick={() => { setSelectedProduct(liveProd); setView('product'); onClose(); }}
                              className="font-serif text-[#584738] text-xs font-semibold mb-1 truncate cursor-pointer hover:underline transition-all duration-200"
                            >
                              {product.name}
                            </h6>
                            <p className="font-sans text-[10px] md:text-[11px] text-[#584738]/70 mb-2 md:mb-3">
                              ₹{product.price.toLocaleString()}
                            </p>
                            {isOutOfStock ? (
                              <button
                                disabled={true}
                                className="w-full py-1.5 text-[9px] tracking-widest font-sans uppercase font-light bg-neutral-300 text-neutral-500 cursor-not-allowed flex items-center justify-center"
                              >
                                Out of Stock
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAddToCart(liveProd)}
                                disabled={addedProductIds[product.id]}
                                className={`w-full py-1.5 text-[9px] tracking-widest font-sans uppercase font-light transition-all duration-300 flex items-center justify-center gap-1.5 ${
                                  addedProductIds[product.id]
                                    ? 'bg-[#3E5C43] text-white cursor-default'
                                    : 'border border-[#584738] text-[#584738] hover:bg-[#584738] hover:text-white'
                                }`}
                              >
                                {addedProductIds[product.id] ? (
                                  <>
                                    <svg className="w-3 h-3 stroke-current animate-bounce" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Added</span>
                                  </>
                                ) : (
                                  <span>Add to Bag</span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
